<template>
  <div class="list">
    <!-- Show search error if searchError -->
    <div class="error" v-if="searchError">
      <span>{{ searchError }}</span>
    </div>
    <!-- Show search result title if searchOn -->
    <div class="title" v-else-if="searchOn">
      <span>Search Results</span>
    </div>
    <div class="title" v-else-if="url.includes('upcoming')">
      <span>Filter Results ({{ displayPopulation.length }}) </span>
    </div>
    <router-link v-else :to="url" :class="{ filter: !title.includes('patients') }">
      {{ title }}
    </router-link>

    <!-- Search -->
    <!-- Native input type for search and icon button -->
    <div class="search">
      <div class="el-input search-input el-input--small">
        <input class="el-input__inner" 
          type="text" 
          v-model="searchInstant" 
          :placeholder="searchPlaceholder" 
          @keyup.enter="doFullSearch()"/>
        <el-button type="primary" 
          icon="el-icon-search" 
          :loading="searchingPatients" 
          class="search-btn" 
          @click="doFullSearch()"></el-button>
          <Transition name="fade">
            <i class="el-icon-close" @click="searchInstant = ''" v-show="searchInstant !== ''"></i>
          </Transition>
      </div>
    </div>

    <div class="sort">
      <span>Sort by</span>
      <el-dropdown @command="setSort" trigger="click">
        <span class="el-dropdown-link">
          <strong>{{ sortDisplay.text }}</strong>
          <i class="el-icon-arrow-down el-icon--right"></i>
        </span>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item
            v-for="index in getUserType === 'observer'
              ? sortIndexForObserver
              : sortIndex"
            :key="index.id"
            :command="index.id"
          >
            {{ index.text }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <div class="loading" v-if="loadingShow || loadingAllProviderPatients || loadingAllProviderLinedownPatients">
      <i class="el-icon-loading"></i>
      <p>Loading {{ type }}s</p>
    </div>
    <template v-else-if="searchOn">
      <div class="no-patient" v-show="searchPopulation.length == 0">
        <img src="../card/image/no-patient.svg" style="margin-left:2px">
        <span>No {{ type }}s found</span>
      </div>
      <div class="list" v-show="searchPopulation.length > 0" @scroll="scrollList">
        <div class="select">
          <PatientSelect class="check" v-model="selectAll"></PatientSelect>
          <span class="text">
            Select all
            <strong>{{ formatCount(this.searchPopulation.length , 'patient') }}</strong>
            <span v-if="selectedPatientList.length">
              ({{ selectedPatientList.length }} selected)
            </span>
          </span>
        </div>
        <div>
          <Patient v-for="patient in searchScrollablePopulation"
            :key="patient.id"
            :patient="patient"
            :url="url"
            :filter="filter"
            :select="select"
            v-bind:sort="sort"
            v-bind:class="{ active: activePatient(patient) }">
          </Patient>
        </div>
      </div>
      <PrintFacesheet
        :allPrintPatients="allPrintPatients"
        :selected-patient-list="selectedPatientList"
        :is-selected-patient="isSelectedPatient"
        @remove-patient="removePatient"
        :get-current-provider="getCurrentProvider"/>
    </template>
    <!-- List of patients -->
    <template v-else>
			<div class="no-patient" v-show="displayPopulation.length == 0">
				<span v-if="startLoadMore && !noMorePatients && this.url.includes('reachout')">
					Loading Patients...
				</span>
				<span v-if="startLoadMore && !noMorePatients && (searchInstant.length > 0 || this.url.includes('reachout'))">
					This might take a while
				</span>
				<img src="../card/image/no-patient.svg" style="margin-left:2px">
				<span>No {{ type }}s found</span>
			</div>

      <div class="list" v-show="displayPopulation.length > 0" @scroll="scrollList" ref="scrollableContainer">
        <div class="select">
          <PatientSelect class="check" v-model="selectAll"></PatientSelect>
          <span class="text">
            Select all
            <strong>{{ formatCount(this.displayPopulation.length , 'patient') }}</strong>
            <span v-if="selectedPatientList.length">
              ({{ selectedPatientList.length }} selected)
            </span>
          </span>
        </div>
        <div>
          <Patient v-for="patient in scrollablePopulation"
            :key="patient.id"
            :patient="patient"
            :url="url"
            :filter="filter"
            :select="select"
            v-bind:sort="sort"
            v-bind:class="{ active: activePatient(patient) }">
          </Patient>
          <template v-if="!noMorePatients && this.url.includes('/patient')">
            <el-button v-if="scrollablePopulation.length === displayPopulation.length"
              :loading="startLoadMore"
              class="load-button" round size="small" @click="doFullSearch(false)" >
              Load more ...
            </el-button>
            <span class="loading-message" 
              v-if="searchInstant.length > 0 && startLoadMore">
              This might take a while 
            </span>
          </template>
        </div>
      </div>

      <div class="action" v-if="getUserType !== 'observer'">
        <el-popover
          placement="bottom"
          width="200"
          v-model="showFacesheetPopover"
          popper-class="facesheet-popover"
          :class="['popover-wrapper', { 'popover-full': !getCurrentProvider }]"
          trigger="click"
        >
          <!-- @click="printFacesheet" -->
          <div class="print-facesheet">
            <div class="print" @click="printFacesheet" style="display: none;">
              Print All ({{ allPrintPatients.length }})
            </div>
            <div
              class="print"
              @click="printActiveFacesheet"
              v-if="activeSelectedPatients.length > 0"
            >
              Print Active ({{ activeSelectedPatients.length }})
            </div>
            <div class="print print-inactive" v-else>
              No Active Patients
            </div>
            <div class="print-cancel" @click="showFacesheetPopover = false">
              Cancel
            </div>
          </div>
          <el-button
            slot="reference"
            type="primary"
            :disabled="!isSelectedPatient"
            :class="{ full: !getCurrentProvider }"
          >
            Print Face Sheet
          </el-button>
        </el-popover>
        <el-button
          v-if="getCurrentProvider"
          class="dark-button"
          :disabled="!isSelectedPatient"
          @click="newMessageDialog = true"
        >
          Send Message
        </el-button>
      </div>
      <MessageDialog
        v-if="getUserType !== 'observer'"
        :patient-list="selectedPatientList"
        :show-message="newMessageDialog"
        @remove-patient="removePatient"
        @close="newMessageDialog = false"
      ></MessageDialog>
    </template>
  </div>
</template>

<script>
import Vue from "vue"
import { mapState, mapActions, mapGetters } from "vuex"
import PopulationMixin from "../mixin/population"
import MessageDialog from "@/module/engagement/common/MessageDialog"

import debounce from "@/module/util/debounce"

import PatientSelect from "./PatientSelect"
// importing printFaceshet from the module
import PrintFacesheet from "./PrintFacesheet.vue"

/**
 * Count for items in a single
 * virtualPage of the list
 */
const VirtualPageCount = 75

import Patient from "./Patient"

import formatCount from "@/module/util/format-count"
import patient from "@/module/patient/card/chart/generate/patient"

export default {
	name: 'population-list',
	mixins: [ PopulationMixin ],

  data() {
    return {
      select: {},
      selectAll: false,
      newMessageDialog: false,
      // Search string filled from the form instantly
      searchInstant: "",
      searchError: false,
      virtualPageNo: 1,
      showFacesheetPopover: false,
      startLoadMore: false,
      searchOn: false,
      searchingPatients: false,
      searchPopulation: [],
    }
  },

  props: {
    loadingShow: {
      type: Boolean,
      default: false,
    }
  },

  watch: {
    selectAll(selected) {
      let selectPatient
      // Assign selectPatient to displayPopulation or
      // searchPopulation depending on the searchOn flag
      if (this.searchOn)
        selectPatient = this.searchPopulation
      else
        selectPatient = this.displayPopulation
      if (selected)
        selectPatient.forEach((patient) => {
          if (patient.name.toLowerCase().includes(this.searchQuery))
            Vue.set(this.select, patient.id, true)
        })
      else
        selectPatient.forEach((patient) => {
          Vue.set(this.select, patient.id, false)
        })
    },
    url: {
      handler(urlValue) {
        this.searchInstant = ""
        if((urlValue.includes('backward')) && !this.loadedAllProviderLinedownPatients)
          this.loadAllProviderLinedownPatients()
      },
      immediate: true
    },
    getCurrentProvider: {
      handler() {
        this.startLoadMore = false
        this.searchInstant = ""
      },
    },
    /**
     * Watch for changes in searchInstant and call
     * doClear if the search string is empty
     */
    searchInstant: {
      handler() {
        if (this.searchInstant.length === 0) {
          this.doClear()
        }
      },
      immediate: true,
    },
    loadedAllProviderPatients: {
      handler(currLoaded) {
        if(currLoaded && this.$props.url.includes('backward') && !this.loadedAllProviderLinedownPatients)
          this.loadAllProviderLinedownPatients()
      }
    }
  },

  methods: {
    formatCount,
    ...mapActions("provider", ["loadProviderPatients", "getSearchPatients", "loadAllProviderLinedownPatients"]),
    printFacesheet() {
      // this.$emit('print', this.select)
      const provider = this.getCurrentProvider
        ? this.getCurrentProvider
        : this.getProvidersIdArray.join(",")
      window.open(
        this.baseUrl +
          "/document/facesheet/?id=" +
          this.allPrintPatients.map(({ id }) => id).join(",") +
          "&provider=" +
          provider
      )
    },
    printActiveFacesheet() {
      const provider = this.getCurrentProvider
        ? this.getCurrentProvider
        : this.getProvidersIdArray[0]
      window.open(
        this.baseUrl +
          `/provider/${provider}/print?document_type=facesheet&ids=` + 
          this.activeSelectedPatients.map(({ next_schedule_id }) => next_schedule_id).join(",")
      )
    },
    removePatient(patientId) {
      Vue.set(this.select, patientId, false)
    },
    /**
     * Checks to see whether list is at the end of scroll and assigns correct
     * `virtualPageNo` to it
     * @param {Object} event Scroll event for the div containing list
     */
    scrollList(event) {
      /**
       * The total pixels left for the bottom to reach
       */
      const pxToBottom =
        event.currentTarget.scrollHeight -
        event.currentTarget.scrollTop -
        event.currentTarget.offsetHeight

      // If the scroll position is 200px from the bottom and there are patients left to render
      if (
        pxToBottom < 400 &&
        VirtualPageCount * this.virtualPageNo < (this.searchOn ? this.searchPopulation.length : this.displayPopulation.length)
      ) {
        this.virtualPageNo++
      }
    },
    /**
     * Load all patients so that the search can be done on all patients
     * if this.url contains `upcoming` or `reachout` search in current patients
     */
    doFullSearch() {
      if(this.url.includes('upcoming') || this.url.includes('reachout')) {
        this.searchPopulation = this.displayPopulation
          .filter(patient => patient.name.toLowerCase().includes(this.searchInstant.toLowerCase()))
        this.searchOn = true
        return
      }
      if(this.searchingPatients) return
      if(this.searchInstant.length < 3) {
        this.searchError = 'Please enter at least 3 characters'
        return
      }
      this.searchError = false
      this.searchingPatients = true
      this.getSearchPatients({ name : this.searchInstant })
        .then((response) => {
          // Assign the response to searchPopulation with sorting
          // from orderingFunction
          this.searchPopulation = response.sort(this.orderingFunction)
        })
        .finally(() => {
          this.searchingPatients = false
          this.searchOn = true
        })
    },
    /**
     * Clear the search if search string is empty
     */
    doClear(event) {
      if(this.searchInstant.length === 0) {
        this.select = {}
        this.searchOn = false
        this.searchPopulation = []
      }
    },
  },

  computed: {
    ...mapState(["baseUrl"]),
    ...mapState("provider", [
      "loadingAllProviderPatients",
      "loadedAllProviderPatients",
      "nextPatients",
      "allPatientCount",
      "loadedAllProviderLinedownPatients",
      "loadingAllProviderLinedownPatients"
    ]),
    ...mapGetters(["getUserType"]),
    isSelectedPatient() {
      return Object.values(this.select).some((selectStatus) => selectStatus)
    },
    selectedPatientList() {
      return Object.keys(this.select)
        .filter((id) => this.select[id])
        .map((id) => {
          // Set searchPatient depending on the searchOn flag
          // If searchOn is true, searchPatient is set to searchPopulation
          // else it is set to displayPopulation
          let searchPatient = this.searchOn ? this.searchPopulation : this.displayPopulation
          const patient = searchPatient.find(
            (patient) => patient.id === id
          )
          return {
            id,
            name: patient.name,
            clinical_data_activity: patient.clinical_data_activity,
            progress: patient.progress,
            next_schedule_id: patient.next_schedule_id
          }
        })
    },
    /**
     * Calculates if any more patients are left to be loaded
     */
    noMorePatients() {
      if (this.getCurrentProvider)
        return this.nextPatients[this.getCurrentProvider] === true
      return Object.values(this.nextPatients).every((noNext) => {
        return noNext === true
      })
    },
    searchedPatientCount() {
      return this.displayPopulation.length
    },
    /**
     * List of patients to be rendered based on current `virtualPageNo`
     * @returns {[*]}
     */
    scrollablePopulation() {
      return this.displayPopulation.slice(
        0,
        VirtualPageCount * this.virtualPageNo
      )
    },
    /**
     * Sorted list of searched patients
     *
     */
     sortedSearchPopulation() {
      return this.searchPopulation.slice().sort(this.orderingFunction)
    },
    /**
     * Scrollable population with search applied
     */
    searchScrollablePopulation() {
      return this.sortedSearchPopulation.slice(
        0,
        VirtualPageCount * this.virtualPageNo
      )
    },
    allPrintPatients() {
      try {
        return this.selectedPatientList.filter(
          ({ progress }) => progress && progress.total !== 0
        )
      } catch (error) {
        console.error("Problem calculating activeSelectedPatients", error)
        return []
      }
    },
    activeSelectedPatients() {
      try {
        const today = new Date()
        const threeMonthsBefore = new Date().setMonth(today.getMonth() - 3)
        return this.selectedPatientList.filter(
          ({ progress }) =>
            progress && progress.total !== 0 && progress.completed !== 0
        )
      } catch (error) {
        console.error("Problem calculating activeSelectedPatients", error)
        return []
      }
    }
  },

  components: {
    Patient,
    PatientSelect,
    MessageDialog,
    PrintFacesheet
  },
}
</script>

<style lang="scss" scoped>
@import "style.scss";
</style>

<style>
.search .el-input-group__append {
  background-color: #2D3E4F;
  border-color: #2D3E4F;
  color: #ffffff;
}
.search .el-input-group__append:hover {
  background-color: #4D657D;
  border-color: #4D657D;
}
.search .el-input__inner {
  padding-left: 10px;
}
.el-popover.facesheet-popover {
  padding: 2px 0px 2px 0px;
  box-shadow: 0 0 20px 3px rgba(0, 0, 0, 0.35);
}
</style>
